import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { cuit: "", nombre: "", tipo: "tercero", telefono: "", observaciones: "" };

export default function TransportistasTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rec/transportistas");
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/rec/transportistas/${editing.id}`, form);
        toast.success("Transportista actualizado");
      } else {
        await api.post("/rec/transportistas", form);
        toast.success("Transportista creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const del = async (it) => {
    if (!confirm(`¿Eliminar transportista "${it.nombre}"?`)) return;
    try {
      await api.delete(`/rec/transportistas/${it.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Catálogo de transportistas</CardTitle>
        <Button size="sm" onClick={openNew} data-testid="new-transportista-btn">
          <Plus size={16} className="mr-1" /> Nuevo transportista
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-neutral-500 py-8">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-neutral-400 py-12" data-testid="transportistas-empty">
            No hay transportistas cargados. Creá uno para poder emitir un REC.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-neutral-500 border-b">
                <tr>
                  <th className="text-left py-2">CUIT</th>
                  <th className="text-left">Nombre</th>
                  <th className="text-left">Tipo</th>
                  <th className="text-left">Teléfono</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b hover:bg-neutral-50" data-testid={`transportista-row-${it.id}`}>
                    <td className="py-2 font-mono">{it.cuit}</td>
                    <td>{it.nombre}</td>
                    <td className="capitalize">{it.tipo}</td>
                    <td>{it.telefono || "-"}</td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(it)}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Nuevo"} transportista</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>CUIT *</Label>
              <Input
                data-testid="transportista-cuit-input"
                value={form.cuit}
                onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                placeholder="20-12345678-9"
              />
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                data-testid="transportista-nombre-input"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="propio">Propio</SelectItem>
                  <SelectItem value="tercero">Tercero</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </div>
            <div>
              <Label>Observaciones</Label>
              <Input value={form.observaciones || ""} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-transportista-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
